import Dexie, { type EntityTable } from 'dexie';
import type { SyncedState } from './syncDecide';
import type { MarkdownDocument, DocSessionState } from '@markbird/shared';

/**
 * Local-first storage. In the MVP the entire document library lives here in
 * IndexedDB — nothing is synced to Firestore unless the user explicitly shares
 * a document. In phase B the same `documents` table becomes the offline mirror
 * of the user's Firestore-synced library (keyed additionally on `ownerId`,
 * which is already part of the schema).
 */

/** A per-document UI session snapshot (cursor/scroll/view mode). */
export type StoredSession = DocSessionState;

/** Singleton app meta (last open doc, etc.), keyed by a fixed string. */
export interface AppMeta {
  key: string;
  lastOpenDocId: string | null;
}

/**
 * A locally-stored image blob, referenced from markdown as `mbimg://<id>`.
 * Images live only on-device (offline-first); they are not uploaded with a
 * shared snapshot (which is plain markdown text).
 */
export interface StoredImage {
  id: string;
  blob: Blob;
  type: string;
  createdAt: number;
}

/**
 * One `[[wikilink]]` occurrence, denormalized for querying.
 *
 * Backlinks used to be computed by regex-scanning the full text of every
 * document on each render. This table is that answer, precomputed: writes touch
 * only the rows of the document being saved, and reads hit an index instead of
 * the library. `toKey` is the normalized link target, which may not resolve to
 * any document -- an unresolved link is a first-class thing to show, not an error.
 */
export interface DocLink {
  id?: number;
  /** Document the link was written in. */
  from: string;
  /** Normalized target title (lowercased). Empty for same-document refs. */
  toKey: string;
  /** Target as written, so a missing note can be created with its real name. */
  rawTarget: string;
  heading: string | null;
  block: string | null;
  /** True for the embed (transclusion) form. */
  embed: boolean;
}

/**
 * A document's metadata, without its body.
 *
 * IndexedDB has no column projection: reading a row reads all of it, so any
 * query over `documents` drags every note's full text into memory. The library
 * list, the tab bar, the quick switcher and the folder tree all want metadata
 * only, and all of them re-run on every autosave. This table is the projection
 * they read instead, so listing a vault costs its metadata rather than its prose.
 *
 * Like `links`, it is derived and disposable: `documents` stays the truth, and a
 * suspected inconsistency is fixed by rebuilding.
 */
export interface DocMeta {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  ownerId: string | null;
  deletedAt?: number | null;
  pinned?: boolean;
  tags: string[];
  aliases: string[];
  folder?: string | null;
  /** Manual-order rank; see `rank.ts`. */
  rank?: string | null;
  /** First line or so of the body, for list previews. */
  excerpt: string;
  /** Body length in characters, for "empty note" affordances and sorting. */
  size: number;
}

/**
 * What a document last agreed on with the cloud (see syncDecide.ts). Derived,
 * like the other projections: losing it only costs one round of re-comparison.
 */
export type { SyncedState } from './syncDecide';

/** Generic key/value row (backup folder handle + settings, feature flags, …). */
export interface KvRow {
  key: string;
  value: unknown;
}

/** A point-in-time version of a document (local history / restore). */
export interface DocSnapshot {
  id: string;
  docId: string;
  title: string;
  content: string;
  createdAt: number;
  /** Why the snapshot was taken: periodic autosave, before a restore, etc. */
  reason: 'auto' | 'restore' | 'remote';
}

const db = new Dexie('markbird') as Dexie & {
  documents: EntityTable<MarkdownDocument, 'id'>;
  sessions: EntityTable<StoredSession, 'docId'>;
  meta: EntityTable<AppMeta, 'key'>;
  images: EntityTable<StoredImage, 'id'>;
  snapshots: EntityTable<DocSnapshot, 'id'>;
  kv: EntityTable<KvRow, 'key'>;
  links: EntityTable<DocLink, 'id'>;
  docmeta: EntityTable<DocMeta, 'id'>;
  syncstate: EntityTable<SyncedState, 'docId'>;
};

db.version(1).stores({
  // `ownerId` is indexed now so the phase-B query `where('ownerId').equals(uid)`
  // needs no migration.
  documents: 'id, updatedAt, ownerId',
  sessions: 'docId',
  meta: 'key',
});

// v2: local image store for pasted/dropped images (referenced as mbimg://id).
db.version(2).stores({
  documents: 'id, updatedAt, ownerId',
  sessions: 'docId',
  meta: 'key',
  images: 'id, createdAt',
});

// v3: per-document version history. Compound index [docId+createdAt] powers
// "snapshots for this doc, newest first".
db.version(3).stores({
  documents: 'id, updatedAt, ownerId',
  sessions: 'docId',
  meta: 'key',
  images: 'id, createdAt',
  snapshots: 'id, docId, createdAt, [docId+createdAt]',
});

// v4: generic key/value store (backup folder handle + settings, etc.).
db.version(4).stores({
  documents: 'id, updatedAt, ownerId',
  sessions: 'docId',
  meta: 'key',
  images: 'id, createdAt',
  snapshots: 'id, docId, createdAt, [docId+createdAt]',
  kv: 'key',
});

// v5: the wikilink index. Derived entirely from document content, so it can be
// dropped and rebuilt at any time -- `documents` remains the only source of truth.
db.version(5).stores({
  documents: 'id, updatedAt, ownerId',
  sessions: 'docId',
  meta: 'key',
  images: 'id, createdAt',
  snapshots: 'id, docId, createdAt, [docId+createdAt]',
  kv: 'key',
  links: '++id, from, toKey',
});

// v6: the metadata projection. `*tags` is a multi-entry index, so filtering the
// library by tag is a lookup rather than a scan.
db.version(6).stores({
  documents: 'id, updatedAt, ownerId',
  sessions: 'docId',
  meta: 'key',
  images: 'id, createdAt',
  snapshots: 'id, docId, createdAt, [docId+createdAt]',
  kv: 'key',
  links: '++id, from, toKey',
  docmeta: 'id, updatedAt, createdAt, folder, deletedAt, *tags',
});

// v7: what each document last agreed on with the cloud, so sync can tell a
// local change from an echo and stop pushing the whole library at every start.
db.version(7).stores({
  documents: 'id, updatedAt, ownerId',
  sessions: 'docId',
  meta: 'key',
  images: 'id, createdAt',
  snapshots: 'id, docId, createdAt, [docId+createdAt]',
  kv: 'key',
  links: '++id, from, toKey',
  docmeta: 'id, updatedAt, createdAt, folder, deletedAt, *tags',
  syncstate: 'docId',
});

export { db };

export const META_KEY = 'app';
